import { Icon } from "@iconify/react/dist/iconify.js";
import { Button, message, Progress, Upload as Uploader } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePostData from "../../api/usePostData";
import { Upload, WhiteLogo } from "../../assets";
import AppInput from "../../common/AppInput";
import AppPhoneInput from "../../common/AppPhoneInput";
import Recaptcha from "../../components/Recaptcha/Recaptcha";
import FocusError from "../../utils/focusOnError";
import { joinAgentValidationSchema } from "../../utils/validations";

/**
 * The main entry point to the join agent, a page where a request to join as an agent is sent to the backend.
 *
 * @returns {JSX.Element}
 */

const JoinAgent = () => {
  const [t] = useTranslation("global");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [fileToUpload, setFileToUploaded] = useState();
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const { postData, loading } = usePostData();
  const featuresData = [
    {
      img: "material-symbols:multiple-airports-outline",
      name: t("professional_airport_text"),
    },
    {
      img: "mdi:book-edit-outline",
      name: t("flexible_booking_text"),
    },
    {
      img: "mdi:cash-sync",
      name: t("multiple_payment_text"),
    },
    {
      img: "material-symbols:eye-tracking-outline-sharp",
      name: t("real_time_text"),
    },
    {
      img: "ic:outline-support-agent",
      name: t("customer_support_text"),
    },
    {
      img: "ci:wavy-check",
      name: t("comfort_text"),
    },
  ];

  const handleUpload = async () => {
    const formData = new FormData();
    formData.append("file", fileToUpload);
    try {
      const res = await postData({
        route: "api/method/airport_transport.api.user.upload_file",
        data: formData,
        onUploadProgress: (event) => {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        },
      });
      return res;
    } catch (err) {
      return err;
    }
  };
  const uploaderProps = {
    name: "file",
    multiple: false,
    accept: "application/pdf",
    maxCount: 1,
    beforeUpload: (file) => {
      const isPdf = file.type === "application/pdf";
      if (!isPdf) {
        message.error(`${file.name} ${t("not_pdf")}`);
        return false || Uploader.LIST_IGNORE;
      }
      if (file.size > 5 * 1024 * 1024) {
        message.error(t("size_exceeded"));
        return false || Uploader.LIST_IGNORE;
      }
      setFileToUploaded(file);
      return false;
    },
    onRemove() {
      setFileToUploaded(null);
    },
  };

  const onSubmit = async (val, { setSubmitting }) => {
    if (!fileToUpload) {
      message.error(t("errors.attachement_error"));
      return false;
    }
    if (recaptchaToken == null) {
      message.error(t("recaptchaRequired"));
      return false;
    }
    const fileRes = await handleUpload();
    const submitData = {
      ...val,
      attachments: fileRes.data.data,
    };
    const requestResult = await postData({
      route: "api/method/airport_transport.api.agent.application",
      data: submitData,
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (requestResult.error) {
      setSubmitting(false);
      message.error(t("errors.submit_error"));
      setRecaptchaToken(null);
      recaptchaRef?.current.reset();
      return;
    }
    navigate("/request-submit");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 py-10 lg:px-20 px-4 gap-10">
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          company_name: "",
          email: "",
          mobile: "",
          company_sector: "",
          city: "",
          country: "",
          description: "",
        }}
        validateOnChange={false}
        validationSchema={joinAgentValidationSchema}
      >
        {({ isSubmitting, setFieldValue, values, errors, touched }) => (
          <Form>
            <AppInput
              label={t("join_agent.company_name")}
              name="company_name"
              type="text"
              placeholder={t("join_agent.enter_company_name")}
              className="mt-3"
              error={errors.company_name}
              touched={touched.company_name}
            />
            <AppInput
              label={t("join_agent.company_email")}
              name="email"
              type="email"
              placeholder={t("join_agent.enter_company_email")}
              className="mt-3"
              error={errors.email}
              touched={touched.email}
            />
            <AppPhoneInput
              label={t("join_agent.company_phone")}
              onChange={(val) => {
                setFieldValue("mobile", val);
              }}
              value={values.mobile}
              name="mobile"
              className={"mb-[15px]"}
              error={errors.mobile}
              touched={touched.mobile}
            />
            <AppInput
              label={t("join_agent.company_sector")}
              name="company_sector"
              type="text"
              placeholder={t("join_agent.enter_company_sector")}
              className="mt-3"
              error={errors.company_sector}
              touched={touched.company_sector}
            />
            <div className="flex gap-4">
              <AppInput
                label={t("join_agent.city")}
                name="city"
                type="text"
                placeholder={t("join_agent.enter_company_city")}
                className="mt-3"
                error={errors.city}
                touched={touched.city}
              />
              <AppInput
                label={t("join_agent.country")}
                name="country"
                type="text"
                placeholder={t("join_agent.enter_company_country")}
                className="mt-3"
                error={errors.country}
                touched={touched.country}
              />
            </div>
            <AppInput
              label={t("join_agent.company_description")}
              name="description"
              type="textArea"
              placeholder={t("join_agent.enter_company_description")}
              className="mt-3"
              error={errors.description}
              touched={touched.description}
            />
            <div>
              <Dragger {...uploaderProps}>
                <img className="m-auto" src={Upload} alt="upload" />
                <p className="ant-upload-text">
                  {t("darg_text")}
                  &nbsp;{t("browse_text")}
                </p>
                <p className="ant-upload-hint">{t("support_text")}</p>
              </Dragger>
            </div>
            {isSubmitting && <Progress percent={uploadProgress} />}
            <Recaptcha
              recaptchaRef={recaptchaRef}
              onChange={(value) => {
                setRecaptchaToken(value);
              }}
            />
            <Button
              type="submit"
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-5 me-2 mb-2 mt-2"
              htmlType="submit"
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
            >
              {t("join_agent.submit_request")}
            </Button>
            <FocusError />
          </Form>
        )}
      </Formik>
      <div className="bg-background_steel_blue text-lg text-text_white p-9 rounded-md h-fit">
        <img className="w-32 m-auto mb-3" src={WhiteLogo} alt="Camelo logo" />
        <p className="text-center font-bold text-2xl mb-12">
          {t("advantagies_text")}
        </p>
        <div className="grid grid-cols-2 gap-8">
          {featuresData.map((feature) => (
            <div
              key={feature.name}
              className="flex items-center text-sm md:text-lg"
            >
              <Icon
                className="md:w-10 md:h-10 h-5 w-5 me-4"
                icon={feature.img}
              />
              <p>{feature.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JoinAgent;
